/**
 * Fleet and command endpoints.
 *
 * Every command returns the same `CommandResult` the server's domain layer
 * produces, so the caller never has to ask whether a drone is simulated. The
 * predecessor branched on that at seven call sites and each new endpoint had to
 * remember to.
 */

import { api, type ApiError, type ApiResult } from '@/api/client';
import { LONG_REQUEST_TIMEOUT_MS } from '@/config';
import type { DroneSnapshotWire } from '@/stores/droneStore';
import type { CommandCode, CommandResult, DroneId, Waypoint } from '@/types/domain';

const COMMAND_KIND: Record<CommandCode, ApiError['kind']> = {
  accepted: 'unknown',
  denied: 'forbidden',
  unsupported: 'invalid',
  failed: 'server',
  timeout: 'timeout',
  not_connected: 'conflict',
  rejected_precondition: 'conflict',
  // A clamped/rejected parameter value is a bad-request-class outcome, the same
  // class kindForStatus() gives 400/422 -- the aircraft understood the write and
  // declined the value, it did not fault.
  value_mismatch: 'invalid',
};

/**
 * Fold a refused command into the same failure shape as a failed request.
 *
 * The aircraft answering "cannot take off: not armed" with HTTP 200 is still a
 * command that did not happen. Leaving that as a second field to check is how
 * the predecessor ended up with call sites that tested `res.ok`, sites that
 * tested `data.success`, and sites that tested neither. One check here, one
 * everywhere.
 */
function unwrapCommand(result: ApiResult<CommandResult>): ApiResult<CommandResult> {
  if (!result.ok) return result;
  const body = result.data;
  if (body && body.ok !== false) return result;
  return {
    ok: false,
    data: null,
    error: {
      kind: body ? COMMAND_KIND[body.code] : 'unknown',
      message: body?.message || 'The aircraft refused the command',
      code: body?.code ?? null,
      detail: body,
    },
    status: result.status,
    latencyMs: result.latencyMs,
  };
}

export function listDrones(): Promise<ApiResult<DroneSnapshotWire[]>> {
  return api.get<DroneSnapshotWire[]>('/drones', { logged: false });
}

export function getDrone(id: DroneId): Promise<ApiResult<DroneSnapshotWire>> {
  return api.get<DroneSnapshotWire>(`/drones/${encodeURIComponent(id)}`, { logged: false });
}

async function command(
  id: DroneId,
  path: string,
  label: string,
  body?: unknown,
): Promise<ApiResult<CommandResult>> {
  return unwrapCommand(
    await api.post<CommandResult>(`/drones/${encodeURIComponent(id)}/${path}`, body, {
      label: `${label} ${id}`,
    }),
  );
}

export const droneCommands = {
  /**
   * Normal arm runs the autopilot's pre-arm checks (GPS, accelerometer,
   * compass). Force is a separate, deliberate call: an ARM button that silently
   * bypasses pre-arm checks is how an aircraft leaves the ground with a bad
   * compass.
   */
  arm: (id: DroneId, force = false) => command(id, 'arm', 'ARM', { force }),
  /**
   * Arm past the autopilot's pre-arm checks and the server's preflight gate.
   * Its own name, like forceDisarm, so the one control that reaches for it is
   * findable and the ARM button can never become it by a default argument.
   */
  forceArm: (id: DroneId) => command(id, 'arm', 'FORCE ARM', { force: true }),
  disarm: (id: DroneId, force = false) => command(id, 'disarm', 'DISARM', { force }),
  /**
   * Cut motors now, bypassing the "is it flying" guard. A multirotor
   * force-disarmed in flight is a controlled crash, not a landing, so this is a
   * separately named call the emergency control reaches for on purpose — never
   * the default DISARM, whose force flag stays off. `disarm(id, true)` was
   * already plumbed and never called; this gives the kill path one honest name.
   */
  forceDisarm: (id: DroneId) => command(id, 'disarm', 'FORCE DISARM', { force: true }),
  takeoff: (id: DroneId, altitudeM: number) =>
    command(id, 'takeoff', 'TAKEOFF', { altitude: altitudeM }),
  land: (id: DroneId) => command(id, 'land', 'LAND'),
  returnToLaunch: (id: DroneId) => command(id, 'rtl', 'RTL'),
  goto: (id: DroneId, lat: number, lon: number, alt: number) =>
    command(id, 'goto', 'GOTO', { lat, lon, alt }),
  /**
   * Circle a point at a radius (MAV_CMD_DO_ORBIT). Unlike a mode change to
   * LOITER, this is point-targeted: the aircraft flies to the ring and orbits
   * the given centre, which is why it belongs on the map as a right-click.
   */
  orbit: (id: DroneId, lat: number, lon: number, radius: number, alt: number) =>
    command(id, 'orbit', 'ORBIT', { lat, lon, radius, alt }),
  /**
   * Point the aircraft's sensor/nose at a location (MAV_CMD_DO_SET_ROI_LOCATION)
   * until cleared. Independent of the flight path — the vehicle can fly its
   * route while keeping the point in frame.
   */
  setRoi: (id: DroneId, lat: number, lon: number, alt: number) =>
    command(id, 'roi', 'ROI', { lat, lon, alt }),
  setMode: (id: DroneId, mode: string) => command(id, 'mode', `MODE ${mode}`, { mode }),
  /**
   * Restart the flight controller. Refused server-side unless the aircraft is
   * known to be disarmed — a reboot in flight is a controlled aircraft
   * becoming a falling object, and that check is not the UI's to make.
   */
  reboot: (id: DroneId) => command(id, 'reboot', 'REBOOT'),
  /**
   * Spin one motor on the bench, to confirm it turns and turns the right way.
   * Bounded server-side: ground only, low throttle, short duration.
   *
   * `confirm` is not decoration. The server answers 409 without it, so this is
   * only ever called from a control that has already asked the operator once —
   * a propeller turning next to someone's hands is not a single-click action.
   */
  testMotor: (id: DroneId, motor: number, throttlePercent: number, durationS: number) =>
    command(id, 'motor-test', `MOTOR ${motor}`, {
      motor,
      throttlePercent,
      durationS,
      confirm: true,
    }),
  /**
   * Move a rover's or boat's steering or throttle on the bench, through
   * ArduRover's own motor test. `percent` is signed: negative is left for the
   * steering and reverse for a throttle. Confirmed for the same reason the
   * motor test is.
   */
  driveTest: (id: DroneId, output: DriveOutput, percent: number, durationS: number) =>
    command(id, 'drive-test', `${output.toUpperCase()} ${percent > 0 ? '+' : ''}${percent}%`, {
      output,
      percent,
      durationS,
      confirm: true,
    }),
};

/** ArduRover's motor test outputs, by role. Mirrors app/domain/surface_vehicle.py. */
export type DriveOutput = 'throttle' | 'steering' | 'throttle_left' | 'throttle_right';

/** Mission transfer lives under /api/missions, not on the drone routes. */
export interface MissionTransfer {
  result: CommandResult;
  /** Empty when the transfer failed, so never render this without checking ok. */
  waypoints: Waypoint[];
}

async function missionCall(
  id: DroneId,
  path: string,
  label: string,
  body?: unknown,
): Promise<ApiResult<MissionTransfer>> {
  const result = await api.post<MissionTransfer>(
    `/missions/${encodeURIComponent(id)}/${path}`,
    body,
    { label: `${label} ${id}`, timeoutMs: LONG_REQUEST_TIMEOUT_MS },
  );
  if (!result.ok) return result;
  const inner = unwrapCommand({ ...result, data: result.data.result });
  return inner.ok ? result : inner;
}

/** Upload an explicit waypoint list. */
export function uploadMission(id: DroneId, waypoints: Waypoint[]) {
  return missionCall(id, 'upload', 'MISSION UPLOAD', { waypoints });
}

/** Upload the drone's route out of a saved plan, without resending the waypoints. */
export function uploadPlanMission(id: DroneId, planId: string) {
  return missionCall(id, 'upload', 'MISSION UPLOAD', { planId });
}

/** Read back what the aircraft actually holds, which then becomes the record. */
export function downloadMission(id: DroneId) {
  return missionCall(id, 'download', 'MISSION DOWNLOAD');
}

export async function startMission(id: DroneId): Promise<ApiResult<CommandResult>> {
  const result = await api.post<{ result: CommandResult }>(
    `/missions/${encodeURIComponent(id)}/start`,
    undefined,
    { label: `MISSION START ${id}` },
  );
  if (!result.ok) return result;
  return unwrapCommand({ ...result, data: result.data.result });
}

/** Hold where it is, keeping the mission and the leg it had reached. */
export async function pauseMission(id: DroneId): Promise<ApiResult<CommandResult>> {
  const result = await api.post<{ result: CommandResult }>(
    `/missions/${encodeURIComponent(id)}/pause`,
    undefined,
    { label: `MISSION PAUSE ${id}` },
  );
  if (!result.ok) return result;
  return unwrapCommand({ ...result, data: result.data.result });
}

/** Continue from the leg it stopped on, not from the start. */
export async function resumeMission(id: DroneId): Promise<ApiResult<CommandResult>> {
  const result = await api.post<{ result: CommandResult }>(
    `/missions/${encodeURIComponent(id)}/resume`,
    undefined,
    { label: `MISSION RESUME ${id}` },
  );
  if (!result.ok) return result;
  return unwrapCommand({ ...result, data: result.data.result });
}

/** The mission last uploaded to a drone, as recorded by the server. */
export function getUploadedMission(
  id: DroneId,
): Promise<ApiResult<{ droneId: string; waypoints: Waypoint[] }>> {
  return api.get(`/missions/${encodeURIComponent(id)}`, { logged: false });
}

/**
 * Fan a command out to several aircraft.
 *
 * Issued in parallel and every result is returned, including the failures. The
 * predecessor ran these in a loop, stopped at the first rejection, and left the
 * operator believing a batch of ten had gone out when three had.
 */
export async function fanOut(
  ids: DroneId[],
  run: (id: DroneId) => Promise<ApiResult<CommandResult>>,
): Promise<Array<{ droneId: DroneId; result: ApiResult<CommandResult> }>> {
  const results = await Promise.all(
    ids.map(async (droneId) => ({ droneId, result: await run(droneId) })),
  );
  return results;
}
